import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate, copyToClipboard } from '../../utils/helpers';
import { Check, X, ExternalLink, Copy, Info, Coins } from 'lucide-react';

// Map a network name to a base explorer URL for a tx hash.
// Returns null when we can't reliably guess (user must open manually).
const EXPLORER_BASES = {
  'bitcoin': 'https://mempool.space/tx/',
  'ethereum': 'https://etherscan.io/tx/',
  'polygon': 'https://polygonscan.com/tx/',
  'bnb smart chain': 'https://bscscan.com/tx/',
  'bnb': 'https://bscscan.com/tx/',
  'bsc': 'https://bscscan.com/tx/',
  'tron': 'https://tronscan.org/#/transaction/',
  'solana': 'https://solscan.io/tx/',
  'arbitrum': 'https://arbiscan.io/tx/',
  'optimism': 'https://optimistic.etherscan.io/tx/',
  'base': 'https://basescan.org/tx/',
  'avalanche': 'https://snowtrace.io/tx/',
  'fantom': 'https://ftmscan.com/tx/',
  'monad': 'https://testnet.monadexplorer.com/tx/',
  'hyperevm': 'https://explorer.hyperliquid.xyz/tx/',
  'ton': 'https://tonviewer.com/transaction/',
  'sui': 'https://suiscan.xyz/mainnet/tx/',
  'aptos': 'https://explorer.aptoslabs.com/txn/',
};

const explorerLinkFor = (network, hash) => {
  if (!hash) return null;
  const trimmed = hash.trim();
  // Already a full URL — respect it
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (!network) return null;
  const base = EXPLORER_BASES[network.toLowerCase().trim()];
  if (!base) return null;
  return base + trimmed;
};

// Extract voucher code from reference field (fallback for old deposits)
const extractVoucherCodeFromReference = (reference) => {
  if (!reference) return null;
  // Reference format: "External Voucher: CODE" or "Provider Name: CODE"
  const match = reference.match(/:\s*(.+)$/);
  return match ? match[1].trim() : null;
};

export const AdminDepositsPage = () => {
  const [status, setStatus] = useState('pending');
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  useEffect(() => { load(); }, [status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDeposits(status);
      console.log('[AdminDeposits] API response:', data); // Debug log
      console.log('[AdminDeposits] Response type:', typeof data, 'Is array:', Array.isArray(data));
      
      // Handle both array and object responses
      const depositsList = Array.isArray(data) ? data : (data.items || data.deposits || []);
      console.log('[AdminDeposits] Processed deposits:', depositsList.length, 'items');
      
      setDeposits(depositsList);
    } catch (error) {
      console.error('[AdminDeposits] Load error:', error);
      console.error('[AdminDeposits] Error response:', error.response?.data);
      toast.error('Failed to load deposits: ' + (error.response?.data?.detail || error.message));
      setDeposits([]);
    } finally { setLoading(false); }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this deposit?')) return;
    try {
      await adminAPI.approveDeposit(id);
      toast.success('Deposit approved');
      setDetails(null);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed');
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await adminAPI.rejectDeposit(id, reason);
      toast.success('Deposit rejected');
      setDetails(null);
      load();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const copy = async (val, label = 'Copied') => {
    if (!val) return;
    await copyToClipboard(val);
    toast.success(label);
  };

  const isCrypto = (d) => (d?.metadata?.voucher_type === 'crypto') || (d?.metadata?.deposit_method === 'crypto');
  const isExternalVoucher = (d) => d?.metadata?.voucher_type === 'external';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-deposits-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Deposit Requests</h1>
          <p className="mt-2 text-muted-foreground">
            Review and approve all deposit requests. Includes external voucher deposits, crypto deposits, and other payment methods. 
            Verify voucher codes, transaction details, and user information before approving.
          </p>
        </div>

        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="deposits-pending-tab">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value={status} className="mt-6">
            <Card>
              <CardContent className="pt-6">
                {loading ? <p className="text-center py-8">Loading...</p> : deposits.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No {status} deposits</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>User ID</TableHead>
                        <TableHead>Deposit Method</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Voucher Code / Reference</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deposits.map((d) => {
                        const crypto = isCrypto(d);
                        const external = isExternalVoucher(d);
                        const network = d.metadata?.network;
                        const txHash = d.metadata?.tx_hash;
                        const link = crypto ? explorerLinkFor(network, txHash) : null;
                        return (
                          <TableRow key={d.id} data-testid={`deposit-${d.id}`}>
                            <TableCell className="text-xs">{formatDate(d.created_at)}</TableCell>
                            <TableCell className="font-mono text-xs">{d.to_user_id?.substring(0, 12)}...</TableCell>
                            <TableCell>
                              {crypto ? (
                                <Badge variant="outline" className="bg-orange-500/15 text-orange-600 border-orange-500/30">
                                  <Coins className="h-3 w-3 mr-1" /> Crypto{network ? ` · ${network}` : ''}
                                </Badge>
                              ) : external ? (
                                <Badge variant="outline">External voucher</Badge>
                              ) : (
                                <Badge variant="outline">Voucher</Badge>
                              )}
                            </TableCell>
                            <TableCell className="font-semibold">{d.currency}</TableCell>
                            <TableCell>{d.amount}</TableCell>
                            <TableCell className="font-mono text-sm max-w-[250px]">
                              {isExternalVoucher(d) ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                                      {d.metadata?.voucher_code || extractVoucherCodeFromReference(d.reference) || 'N/A'}
                                    </span>
                                    {(d.metadata?.voucher_code || extractVoucherCodeFromReference(d.reference)) && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copy(d.metadata?.voucher_code || extractVoucherCodeFromReference(d.reference), 'Voucher code copied');
                                        }}
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                  {d.metadata?.provider_name && (
                                    <div className="text-xs text-muted-foreground">via {d.metadata.provider_name}</div>
                                  )}
                                </div>
                              ) : (
                                <div className="truncate" title={d.reference}>{d.reference || '-'}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={d.status === 'approved' ? 'bg-green-600' : d.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'}>
                                {d.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1 whitespace-nowrap">
                              {crypto && link && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                                  data-testid={`verify-explorer-${d.id}`}
                                  title={`Open on ${network} explorer`}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" /> Verify
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDetails(d)}
                                data-testid={`details-deposit-${d.id}`}
                              >
                                <Info className="h-3 w-3 mr-1" /> Details
                              </Button>
                              {status === 'pending' && (
                                <>
                                  <Button size="sm" variant="outline" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approve(d.id)} data-testid={`approve-deposit-${d.id}`}>
                                    <Check className="h-3 w-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => reject(d.id)} data-testid={`reject-deposit-${d.id}`}>
                                    <X className="h-3 w-3 mr-1" /> Reject
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Details dialog */}
      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent className="max-w-lg" data-testid="deposit-details-dialog">
          <DialogHeader>
            <DialogTitle>Deposit details</DialogTitle>
            <DialogDescription>Review the submission details before approving.</DialogDescription>
          </DialogHeader>
          {details && (
            <div className="space-y-3 text-sm">
              <DetailRow label="Amount" value={`${details.amount} ${details.currency}`} />
              <DetailRow label="Status" value={<Badge className={details.status === 'approved' ? 'bg-green-600' : details.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'}>{details.status}</Badge>} />
              <DetailRow label="Reference" value={details.reference} />
              <DetailRow label="User ID" value={details.to_user_id} mono copyable onCopy={() => copy(details.to_user_id, 'User ID copied')} />
              <DetailRow label="Submitted" value={formatDate(details.created_at)} />

              {isCrypto(details) && (
                <>
                  <div className="pt-2 mt-2 border-t border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Crypto details</p>
                  </div>
                  <DetailRow label="Source" value={details.metadata?.source} />
                  <DetailRow label="Network" value={details.metadata?.network} />
                  <DetailRow label="Our deposit address" value={details.metadata?.deposit_address} mono copyable onCopy={() => copy(details.metadata?.deposit_address, 'Address copied')} />
                  {details.metadata?.memo_tag && (
                    <DetailRow label="Memo/Tag" value={details.metadata?.memo_tag} mono />
                  )}
                  <DetailRow
                    label="Transaction hash"
                    value={details.metadata?.tx_hash}
                    mono
                    copyable
                    onCopy={() => copy(details.metadata?.tx_hash, 'Hash copied')}
                  />
                  {(() => {
                    const link = explorerLinkFor(details.metadata?.network, details.metadata?.tx_hash);
                    if (!link) {
                      return <p className="text-xs text-muted-foreground">No explorer known for &quot;{details.metadata?.network}&quot; — verify manually.</p>;
                    }
                    return (
                      <Button
                        size="sm"
                        onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                        data-testid="verify-tx-explorer-btn"
                        className="w-full"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" /> Verify on {details.metadata?.network} explorer
                      </Button>
                    );
                  })()}
                  {details.metadata?.user_note && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">User note</p>
                      <p className="text-sm bg-muted/60 rounded p-2 whitespace-pre-wrap">{details.metadata.user_note}</p>
                    </div>
                  )}
                </>
              )}

              {isExternalVoucher(details) && (
                <>
                  <div className="pt-2 mt-2 border-t border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">📋 External Voucher Deposit</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-2">Voucher Code</p>
                    <div className="flex items-center gap-2">
                      <code className="text-lg font-bold font-mono text-blue-700 dark:text-blue-400">
                        {details.metadata?.voucher_code || extractVoucherCodeFromReference(details.reference) || 'N/A'}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copy(details.metadata?.voucher_code || extractVoucherCodeFromReference(details.reference), 'Voucher code copied')}
                      >
                        <Copy className="h-3 w-3 mr-1" /> Copy
                      </Button>
                    </div>
                  </div>
                  <DetailRow label="Payment Provider" value={details.metadata?.provider_name || 'External Voucher'} />
                  {details.metadata?.provider_code && (
                    <DetailRow label="Provider Code" value={details.metadata.provider_code} mono />
                  )}
                  {details.metadata?.user_note && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">User Note</p>
                      <p className="text-sm bg-muted/60 rounded p-2 whitespace-pre-wrap">{details.metadata.user_note}</p>
                    </div>
                  )}
                  <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded border border-yellow-200 dark:border-yellow-800">
                    <p className="text-xs text-yellow-800 dark:text-yellow-300">
                      ⚠️ Verify this voucher code with the payment provider before approving. Ensure the amount matches and the transaction is legitimate.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            {details?.status === 'pending' && (
              <>
                <Button variant="outline" className="text-red-600" onClick={() => reject(details.id)}><X className="h-4 w-4 mr-1" /> Reject</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approve(details.id)} data-testid="dialog-approve-btn"><Check className="h-4 w-4 mr-1" /> Approve</Button>
              </>
            )}
            {details?.status !== 'pending' && (
              <Button variant="outline" onClick={() => setDetails(null)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

const DetailRow = ({ label, value, mono, copyable, onCopy }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="grid grid-cols-3 gap-2 items-start">
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      <div className="col-span-2 flex items-start gap-2">
        <div className={`flex-1 break-all ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</div>
        {copyable && (
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onCopy} type="button">
            <Copy className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};
